/**
 * Theme Context for Report System
 * 
 * Provides centralized theme management and CSS variable injection
 * for consistent styling across all report components.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export interface ThemeTokens {
  [key: string]: string;
}

export interface BrandingKit {
  id: string;
  name: string;
  description?: string;
  is_default?: boolean;
  client_id?: string;
  project_id?: string;
  colors?: {
    primary: string;
    secondary: string;
    success: string;
    warning: string;
    danger: string;
    background: string;
    text: string;
  };
  fonts?: {
    heading: string;
    body: string;
    fallback: string;
  };
  logo?: {
    url: string;
    width: number;
    height: number;
  };
  created_at?: string;
  updated_at?: string;
  branding?: any;
  compliance_rules?: any[];
}

export interface ThemeContextType {
  currentTheme: string;
  setTheme: (themeName: string) => void;
  availableThemes: string[];
  tokens: ThemeTokens;
  getComponentStyles: (component: string) => Record<string, any>;
  getStatusStyles: (status: string) => Record<string, any>;
  getPrintStyles: () => Record<string, any>;
  // Additional properties for branding management
  theme?: any;
  branding?: BrandingKit | null;
  setBranding?: (branding: BrandingKit | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: string;
}

// Default theme tokens (fallback)
const DEFAULT_TOKENS: ThemeTokens = {
  '--color-primary': '#1e40af',
  '--color-secondary': '#64748b',
  '--color-success': '#059669',
  '--color-warning': '#d97706',
  '--color-error': '#dc2626',
  '--color-text-primary': '#0f172a',
  '--color-text-secondary': '#475569',
  '--color-background': '#ffffff',
  '--color-surface': '#f8fafc',
  '--font-size-base': '1rem',
  '--font-size-lg': '1.125rem',
  '--font-size-xl': '1.25rem',
  '--font-size-2xl': '1.5rem',
  '--font-size-3xl': '1.875rem',
  '--spacing-2': '0.5rem',
  '--spacing-3': '0.75rem',
  '--spacing-4': '1rem',
  '--spacing-6': '1.5rem',
  '--spacing-8': '2rem',
  '--border-radius-base': '0.25rem',
  '--border-radius-lg': '0.5rem',
  '--shadow-sm': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  '--shadow-base': '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
};

// Available themes
const AVAILABLE_THEMES = ['default', 'corporate', 'minimal'];

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  defaultTheme = 'default' 
}) => {
  const [currentTheme, setCurrentTheme] = useState<string>(defaultTheme);
  const [tokens, setTokens] = useState<ThemeTokens>(DEFAULT_TOKENS);

  // Flatten theme data into CSS variables
  const flattenTokens = (themeData: any, prefix = '--'): ThemeTokens => {
    const flattened: ThemeTokens = {};
    
    const flatten = (obj: any, keyPrefix: string) => {
      for (const [key, value] of Object.entries(obj)) {
        const cssVar = `${keyPrefix}${key}`;
        if (typeof value === 'object' && value !== null) {
          flatten(value, `${cssVar}-`);
        } else {
          flattened[cssVar] = String(value);
        }
      }
    };
    
    flatten(themeData, prefix);
    return flattened;
  };

  // Update tokens when theme changes
  useEffect(() => {
    // For now, use default tokens
    // In a real app, this would load from the backend
    setTokens(DEFAULT_TOKENS);
    
    // Apply CSS variables to document root
    const root = document.documentElement;
    Object.entries(DEFAULT_TOKENS).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [currentTheme]);

  const setTheme = (themeName: string) => {
    if (AVAILABLE_THEMES.includes(themeName)) {
      setCurrentTheme(themeName);
    } else {
      console.warn(`Theme "${themeName}" not found. Available themes:`, AVAILABLE_THEMES);
    }
  };

  const getComponentStyles = (component: string): Record<string, any> => {
    // Return default component styles
    return {};
  };

  const getStatusStyles = (status: string): Record<string, any> => {
    // Return default status styles
    return {};
  };

  const getPrintStyles = (): Record<string, any> => {
    // Return default print styles
    return {};
  };

  const value: ThemeContextType = {
    currentTheme,
    setTheme,
    availableThemes: AVAILABLE_THEMES,
    tokens,
    getComponentStyles,
    getStatusStyles,
    getPrintStyles,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- useTheme is hook, ThemeProvider is component
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;